import {
  Box,
  Paper,
  Typography,
  Grid,
  Breadcrumbs,
  Link,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Skeleton,
  useMediaQuery,
  Avatar,
  Chip,
} from '@mui/material';
import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetPaymentListQuery, useGetPaymentLogsByIdQuery } from '@/services/paymentApi';
import { useSnackbar } from 'notistack';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Tabs, Tab } from '@mui/material';
import AxisLogo from '../icons/Axis.svg';
import MiraeLogo from '../icons/Mirae.svg';
import InvescoLogo from '../icons/Invesco.svg';
import LumpsumIcon from '../icons/Lumpsum.svg';
import SIPIcon from '../icons/Sip.svg';
import LumpsumSIPIcon from '../icons/Lumpsip.svg';
import BajajLogo from '../icons/bajaj.png';
import MotilalLogo from '../icons/Motilal.png';
import { LogoBox } from '../components/LogoBox';
import { ContentCopy, InfoOutlined } from '@mui/icons-material';
import JSONPretty from 'react-json-pretty';
import 'react-json-pretty/themes/monikai.css';
import { getAggregatorLogo } from '@/constants/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { PaymentLog } from '@/services/paymentApi';
const GROUPED_LOG_TITLES = [
  'Checking Transaction Status - QueryAPI',
  'Setteling Through Requery',
];

type PaymentDetailsResponse = {
  id: string;
  ihNoOrCart: string;
  amount: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  trtype: string;
  gateway: string;
  swiftFlowErrorCode?: string | null;
  message?: string | null;
  amc: {
    name: string;
    logoPath?: string;
  };
};

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: true,
  });
}

function formatDeltaTime(ms: number): string {
  const absMs = Math.abs(ms);
  if (absMs < 1000) return `${absMs}ms`;
  if (absMs < 60000) return `${(absMs / 1000).toFixed(2)}s`;
  if (absMs < 3600000) {
    const mins = Math.floor(absMs / 60000);
    const secs = Math.floor((absMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  }
  const hours = Math.floor(absMs / 3600000);
  const mins = Math.floor((absMs % 3600000) / 60000);
  return `${hours}h ${mins}m`;
}

export default function PaymentDetail() {
  const { id } = useParams<{ id: string }>();
  const {
    data,
    isLoading: isPaymentLoading,
    isError: isPaymentError,
  } = useGetPaymentListQuery({ search: id || '', page: 1, limit: 1 });
  const payment = data?.data[0];
  const paymentId = payment?.id;
  const { data: paymentLogsData, isLoading: isPaymentLogsLoading } = useGetPaymentLogsByIdQuery(
    paymentId ?? skipToken
  );
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showDialog, setShowDialog] = useState(false);
  const [selectedLog, setSelectedLog] = useState<PaymentLog | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [showOlderLogsDialog, setShowOlderLogsDialog] = useState(false);

  const processedLogsData = useMemo(() => {
    if (!paymentLogsData?.logs) return { processedLogs: [], olderQueryLogs: [] };

    const queryLogs = [...paymentLogsData.logs]
      .filter((l) => GROUPED_LOG_TITLES.includes(l.title))
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

    if (queryLogs.length <= 1) {
      return { processedLogs: paymentLogsData.logs, olderQueryLogs: [] };
    }

    const latestQueryLog = queryLogs[queryLogs.length - 1];
    const olderQueryLogs = queryLogs.filter((l) => l.id !== latestQueryLog.id).reverse();

    const processedLogs = paymentLogsData.logs.filter((l) => {
      if (GROUPED_LOG_TITLES.includes(l.title)) {
        return l.id === latestQueryLog.id;
      }
      return true;
    });

    return { processedLogs, olderQueryLogs };
  }, [paymentLogsData?.logs]);

  const { processedLogs, olderQueryLogs } = processedLogsData;

  const showLogsInfo = (log: PaymentLog) => {
    setSelectedLog(log);
    setShowDialog(true);
  };
  const handleCloseDialog = () => {
    setShowDialog(false);
    setSelectedLog(null);
  };

  const handleCloseOlderLogsDialog = () => {
    setShowOlderLogsDialog(false);
  };
  const getAssetLogo = (amc: { name: string; logoPath?: string }) => {
    if (amc.logoPath) {
      return (
        <img
          src={amc.logoPath}
          alt={amc.name}
          style={{ width: 32, height: 32, objectFit: 'contain' }}
        />
      );
    }

    const assetName = amc.name;
    const isBajajOrMotilal = assetName.includes('BAJAJ') || assetName.includes('Motilal');
    const logo = (() => {
      switch (assetName) {
        case 'BAJAJ FINSERV Mutual Fund':
          return BajajLogo;
        case 'Axis Mutual Fund':
          return AxisLogo;
        case 'Mirae Asset Mutual Fund':
          return MiraeLogo;
        case 'Motilal Oswal Mutual Fund':
          return MotilalLogo;
        case 'Invesco Mutual Fund':
          return InvescoLogo;
        default:
          return null;
      }
    })();

    if (logo) {
      if (isBajajOrMotilal) {
        return <LogoBox logo={logo} />;
      }
      return <img src={logo} alt="" />;
    }

    return (
      <Avatar sx={{ width: 32, height: 32, fontSize: '14px', bgcolor: theme.palette.primary.main }}>
        {assetName.charAt(0).toUpperCase()}
      </Avatar>
    );
  };

  const getTransactionTypeLogo = (typeName: string) => {
    switch (typeName.toUpperCase()) {
      case 'LUMPSUM':
        return LumpsumIcon;
      case 'SIP':
        return SIPIcon;
      case 'HYBRID':
        return LumpsumSIPIcon;
      default:
        return LumpsumIcon; // Default logo
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCopyClick = () => {
    if (!selectedLog) return;
    const json = JSON.stringify(selectedLog.payload, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      enqueueSnackbar('Payload copied to clipboard!', {
        variant: 'success',
      });
    });
  };

  if (isPaymentLoading || isPaymentLogsLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width={300} height={30} sx={{ mb: 2 }} />

        {/* Payment Details Skeleton */}
        <Paper sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
          <Grid container spacing={3}>
            {[...Array(8)].map((_, i) => (
              <Grid item xs={12} md={3} key={i}>
                <Skeleton variant="text" width="80%" height={30} />
                <Skeleton variant="rectangular" width="100%" height={60} sx={{ mt: 1 }} />
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Payment Logs Skeleton */}
        <Paper sx={{ p: 3, borderRadius: '16px' }}>
          <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
          ))}
        </Paper>
      </Box>
    );
  }

  if (isPaymentError || !payment) {
    return <Typography>Error loading payment details</Typography>;
  }

  const paymentData = payment as unknown as PaymentDetailsResponse;
  const failureInfo =
    paymentData.status?.toLowerCase() === 'failure'
      ? {
        code: paymentData.swiftFlowErrorCode?.trim() || '',
        message: paymentData.message?.trim() || paymentData.swiftFlowErrorCode?.trim() || '',
      }
      : null;

  const getIconByStatus = (status: string) => {
    if (status === 'success' || status === 'success umrn update') {
      return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
    } else if (status === 'in_progress') {
      return <HourglassEmptyIcon sx={{ color: theme.palette.warning.main }} />;
    } else if (status === 'failure') {
      return <ErrorIcon sx={{ color: theme.palette.error.main }} />;
    }
    return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Dialog open={showDialog} onClose={handleCloseDialog} maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">Payload</Typography>
          <IconButton onClick={handleCopyClick} color="primary" size="small">
            <ContentCopy />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <JSONPretty id="json-pretty" json={selectedLog?.payload}></JSONPretty>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs
        separator="·"
        sx={{
          mb: 1,
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
            color: theme.palette.text.secondary,
          },
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Dashboard
        </Link>
        <Link
          component={RouterLink}
          to="/payments"
          sx={{
            color: theme.palette.primary.main,
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.subtitle1.fontWeight,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          Payments
        </Link>
        <Typography
          color="text.primary"
          sx={{
            fontSize: theme.typography.subtitle1.fontSize,
            fontWeight: theme.typography.fontWeightMedium,
          }}
        >
          Payment Details
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 3, mb: 3, borderRadius: '16px', boxShadow: 'none' }}>
        {/* Top row with Amount and Order ID */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
            pb: 2,
            borderBottom: `1px dashed ${theme.palette.divider}`,
            flexDirection: isMobile ? 'column' : 'row',
          }}
        >
          <Box sx={{ mb: isMobile ? 2 : 0 }}>
            <Typography variant="subtitle1" gutterBottom>
              Amount
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h1" fontWeight="bold">
                ₹ {paymentData.amount || 0}
              </Typography>
              <Box
                sx={{
                  backgroundColor:
                    paymentData.status === 'success'
                      ? theme.palette.success.light
                      : paymentData.status === 'settled'
                        ? theme.palette.success.light
                        : paymentData.status === 'pending'
                          ? theme.palette.warning.light
                          : theme.palette.error.light,
                  color:
                    paymentData.status === 'success'
                      ? theme.palette.success.main
                      : paymentData.status === 'settled'
                        ? theme.palette.success.main
                        : paymentData.status === 'pending'
                          ? theme.palette.warning.main
                          : theme.palette.error.main,
                  borderRadius: '36px',
                  padding: '6px 16px 6px 16px',
                  fontSize: theme.typography.subtitle1.fontSize,
                  fontWeight: theme.typography.h1.fontWeight,
                }}
              >
                {paymentData.status.toLocaleUpperCase()}
              </Box>
            </Box>
          </Box>
          <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
            <Typography variant="subtitle1" gutterBottom>
              Order ID
            </Typography>
            <Typography variant="h4">{paymentData.ihNoOrCart}</Typography>
          </Box>
        </Box>

        {/* Bottom grid with other details */}
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              Transaction Type
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src={getTransactionTypeLogo(paymentData.trtype)} alt="" />
              <Typography variant="h4">{paymentData.trtype}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              Payment Aggregator
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="img"
                src={getAggregatorLogo(paymentData.gateway)}
                alt={`${paymentData.gateway} logo`}
                sx={{ width: 32, height: 32, objectFit: 'contain' }}
              />
              <Typography variant="h4">{paymentData.gateway}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              AMC
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getAssetLogo(paymentData.amc)}
              <Typography variant="h4">{paymentData.amc.name}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              Asset
            </Typography>
             <Typography variant="h4">
                  {payment?.assetName}
                  </Typography>
          
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              Transaction No
            </Typography>
            <Typography variant="h4">{paymentData.id}</Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              Created at
            </Typography>
            <Typography variant="h4">{formatDate(paymentData.createdAt)}</Typography>
          </Grid>

          <Grid item xs={12} md={3}>
            <Typography variant="subtitle1" gutterBottom>
              Updated at
            </Typography>
            <Typography variant="h4">{formatDate(paymentData.updatedAt)}</Typography>
          </Grid>
          {failureInfo?.message && (
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" gutterBottom>
                Message
              </Typography>
              <Typography variant="h4" sx={{ wordBreak: 'break-word' }}>
                {failureInfo.message}
              </Typography>
            </Grid>
          )}
          {failureInfo?.code && (
            <Grid item xs={12} md={3}>
              <Typography variant="subtitle1" gutterBottom>
                SwiftFlow Error Code
              </Typography>
              <Typography variant="h4" sx={{ wordBreak: 'break-word' }}>
                {failureInfo.code}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper sx={{ px: 3, pb: 3, borderRadius: '16px', boxShadow: 'none' }}>
        {/* Tabs for Transaction Details and Refund Details */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{
            mb: 3,
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.common.black,
              height: 3,
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '16px',
              color: theme.palette.common.black,
              '&.Mui-selected': {
                color: theme.palette.common.black,
              },
            },
          }}
        >
          <Tab
            icon={
              <Box
                component="span"
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor:
                    tabValue === 0 ? theme.palette.common.black : theme.palette.grey[400],
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  fontSize: theme.typography.subtitle1.fontSize,
                }}
              >
                ₹
              </Box>
            }
            iconPosition="start"
            label="Transaction Details"
            sx={{ fontSize: theme.typography.subtitle1.fontSize }}
          />
          <Tab
            icon={
              <Box
                component="span"
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor:
                    tabValue === 1 ? theme.palette.common.black : theme.palette.grey[400],
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 1,
                  fontSize: theme.typography.subtitle1.fontSize,
                }}
              >
                ↺
              </Box>
            }
            iconPosition="start"
            label="Refund Details"
            sx={{ fontSize: theme.typography.subtitle1.fontSize }}
          />
        </Tabs>

        {/* Transaction Timeline */}
        {tabValue === 0 && (
          <Box sx={{ maxWidth: 600 }}>
            <Stepper
              orientation="vertical"
              activeStep={-1} // Set to -1 to show all steps
            >
              {processedLogs.map((log, index) => (
                <Step expanded={true} key={log.id || index}>
                  <StepLabel icon={getIconByStatus(log.status.toLowerCase())}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="h4" sx={{ color: theme.palette.primary.main }}>
                        {log.title}
                      </Typography>
                      {log.payload && (
                        <IconButton
                          size="medium"
                          color="primary"
                          sx={{ ml: 0.5 }}
                          onClick={() => showLogsInfo(log)}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      )}
                      {GROUPED_LOG_TITLES.includes(log.title) && olderQueryLogs.length > 0 && (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ml: 2, textTransform: 'none', borderRadius: '20px' }}
                          onClick={() => setShowOlderLogsDialog(true)}
                        >
                          View Older Logs ({olderQueryLogs.length})
                        </Button>
                      )}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: theme.typography.h1.fontWeight }}
                    >
                      {log.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: theme.typography.subtitle1.fontWeight,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {(() => {
                          const date = new Date(log.createdAt);
                          const formattedDate = date.toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            hour12: true,
                          });
                          const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
                          return formattedDate.replace(/(\d{2})\s+(AM|PM)/, `$1.${milliseconds} $2`);
                        })()}
                      </Typography>
                      {(() => {
                        const nextLog = processedLogs[index + 1];
                        if (!nextLog) return null;
                        const currentTime = new Date(log.createdAt).getTime();
                        const nextTime = new Date(nextLog.createdAt).getTime();
                        const deltaMs = currentTime - nextTime;
                        return (
                          <Chip
                            label={`+${formatDeltaTime(deltaMs)}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              backgroundColor: `${theme.palette.info.light}40`,
                              color: theme.palette.info.dark,
                              border: `1px solid ${theme.palette.info.light}`,
                            }}
                          />
                        );
                      })()}
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        {/* Older QueryAPI Logs Dialog */}
        <Dialog
          open={showOlderLogsDialog}
          onClose={handleCloseOlderLogsDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Older Transaction Status Logs</DialogTitle>
          <DialogContent dividers>
            <Stepper orientation="vertical">
              {olderQueryLogs.map((log, index) => (
                <Step expanded={true} key={log.id || index}>
                  <StepLabel icon={getIconByStatus(log.status.toLowerCase())}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6" color="primary">
                        {log.title}
                      </Typography>
                      {log.payload && (
                        <IconButton
                          size="small"
                          color="primary"
                          sx={{ ml: 1 }}
                          onClick={() => showLogsInfo(log)}
                        >
                          <InfoOutlined fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2">{log.description}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {(() => {
                          const date = new Date(log.createdAt);
                          const formattedDate = date.toLocaleString('en-US', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: 'numeric',
                            second: 'numeric',
                            hour12: true,
                          });
                          const milliseconds = date.getMilliseconds().toString().padStart(3, '0');
                          return formattedDate.replace(/(\d{2})\s+(AM|PM)/, `$1.${milliseconds} $2`);
                        })()}
                      </Typography>
                      {(() => {
                        const nextLog = olderQueryLogs[index + 1];
                        if (!nextLog) return null;
                        const currentTime = new Date(log.createdAt).getTime();
                        const nextTime = new Date(nextLog.createdAt).getTime();
                        const deltaMs = currentTime - nextTime;
                        return (
                          <Chip
                            label={`+${formatDeltaTime(deltaMs)}`}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              backgroundColor: `${theme.palette.info.light}40`,
                              color: theme.palette.info.dark,
                              border: `1px solid ${theme.palette.info.light}`,
                            }}
                          />
                        );
                      })()}
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseOlderLogsDialog}>Close</Button>
          </DialogActions>
        </Dialog>
        {/* Refund Details (empty for now) */}
        {tabValue === 1 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body1" color="text.secondary">
              No refund details available for this transaction.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
